import React, {
  useState, useEffect, useCallback,
} from 'react';
import {
  Modal, Form, Button, Row, Col, Table, Badge,
} from 'react-bootstrap';
import _ from 'lodash';
import Error from '../../../Components/Error';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import setStateFromInput from '../../../Utils/setStateFromInput';
import PricingService from '../../../Services/PricingService';
import { ServiceAreaPreviewMap } from '../../../Components/PricingGeo';

function PredefinedRouteModal({
  originalRoute = {},
  show = false,
  handleClose,
  fetchRoutes,
  vehicleCategories = [],
  serviceAreas = [],
}) {
  const defaultRoute = {
    active: 1,
    is_bidirectional: true,
    category_prices: [],
  };

  const [route, setRoute] = useState(_.defaults(_.cloneDeep(originalRoute), defaultRoute));
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalReady, setModalReady] = useState(false);

  // Reset route state when originalRoute changes (when editing a different route)
  useEffect(() => {
    setRoute(_.defaults(_.cloneDeep(originalRoute), defaultRoute));
    setError('');
    setValidationErrors([]);
  }, [originalRoute]);

  // Get geographic data for a service area
  const getGeoData = useCallback((serviceAreaId) => {
    if (!serviceAreaId) return null;
    const sa = serviceAreas.find((s) => s.Id === serviceAreaId);
    if (!sa?.geographic_definition) return null;
    return {
      type: sa.geographic_definition.type,
      center: sa.geographic_definition.center,
      radius_km: sa.geographic_definition.radius_km,
      polygon_coordinates: sa.geographic_definition.polygon_coordinates,
      name: sa.name,
    };
  }, [serviceAreas]);

  // Reset modalReady when modal closes
  useEffect(() => {
    if (!show) {
      setModalReady(false);
    }
  }, [show]);

  // Initialize category prices for all vehicle categories if not set
  useEffect(() => {
    if (vehicleCategories.length > 0 && route.category_prices.length === 0) {
      const defaultPrices = vehicleCategories.map((cat) => ({
        vehicle_category_id: cat.Id,
        price_eur: 0,
        price_huf: 0,
      }));
      setRoute((prev) => ({ ...prev, category_prices: defaultPrices }));
    }
  }, [vehicleCategories]);

  const handleSubmit = async () => {
    setLoading(true);

    try {
      await PricingService.createOrUpdatePredefinedRoute({
        ...route,
        active: Number(route.active),
        is_bidirectional: Boolean(route.is_bidirectional),
        category_prices: route.category_prices.map((cp) => ({
          vehicle_category_id: cp.vehicle_category_id,
          price_eur: Number(cp.price_eur || 0),
          price_huf: Number(cp.price_huf || 0),
        })),
      });
      fetchRoutes();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message);
      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setStateFromInput(e, setRoute, route);
  };

  const handleCategoryPriceChange = (categoryId, field, value) => {
    setRoute((prev) => {
      const updatedPrices = [...prev.category_prices];
      const index = updatedPrices.findIndex((cp) => cp.vehicle_category_id === categoryId);

      if (index >= 0) {
        updatedPrices[index] = {
          ...updatedPrices[index],
          [field]: value,
        };
      } else {
        updatedPrices.push({
          vehicle_category_id: categoryId,
          [field]: value,
          price_eur: 0,
          price_huf: 0,
        });
      }

      return { ...prev, category_prices: updatedPrices };
    });
  };

  const getCategoryPrice = (categoryId, field) => {
    const cp = route.category_prices.find((p) => p.vehicle_category_id === categoryId);
    return cp ? cp[field] || '' : '';
  };

  const originGeo = getGeoData(route.origin_service_area_id);
  const destGeo = getGeoData(route.destination_service_area_id);
  const showPreview = Boolean(route.origin_service_area_id || route.destination_service_area_id);

  return (
    <Modal show={show} onHide={handleClose} size="xl" onEntered={() => setModalReady(true)}>
      <Modal.Header closeButton>
        <Modal.Title>
          {route.Id ? `Edit ${route.name}` : 'New Predefined Route'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />

        <Row>
          <Col lg={6}>
            <Form>
              <Form.Group className="mb-3" controlId="name">
                <Form.Label>Route Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={route.name || ''}
                  onChange={handleChange}
                  placeholder="e.g., Budapest Airport - City Center"
                />
                <FormControlFeedback errors={validationErrors} field="name" />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="origin_service_area_id">
                    <Form.Label>Origin *</Form.Label>
                    <Form.Select
                      value={route.origin_service_area_id || ''}
                      onChange={handleChange}
                    >
                      <option value="">Select origin</option>
                      {serviceAreas.map((sa) => (
                        <option key={sa.Id} value={sa.Id}>
                          {sa.name}
                        </option>
                      ))}
                    </Form.Select>
                    <FormControlFeedback errors={validationErrors} field="origin_service_area_id" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="destination_service_area_id">
                    <Form.Label>Destination *</Form.Label>
                    <Form.Select
                      value={route.destination_service_area_id || ''}
                      onChange={handleChange}
                    >
                      <option value="">Select destination</option>
                      {serviceAreas.map((sa) => (
                        <option key={sa.Id} value={sa.Id}>
                          {sa.name}
                        </option>
                      ))}
                    </Form.Select>
                    <FormControlFeedback errors={validationErrors} field="destination_service_area_id" />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="is_bidirectional">
                    <Form.Label>Bidirectional?</Form.Label>
                    <Form.Select
                      value={route.is_bidirectional ? '1' : '0'}
                      onChange={(e) => setRoute((prev) => ({
                        ...prev,
                        is_bidirectional: e.target.value === '1',
                      }))}
                    >
                      <option value="1">Yes (Same price both ways)</option>
                      <option value="0">No (One direction only)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="active">
                    <Form.Label>Status</Form.Label>
                    <Form.Select value={route.active} onChange={handleChange}>
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3" controlId="description">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={route.description || ''}
                  onChange={handleChange}
                />
              </Form.Group>

              <hr />
              <h5 className="mb-3">Fixed Prices by Vehicle Category</h5>

              <Table responsive bordered size="sm">
                <thead>
                  <tr>
                    <th>Vehicle Category</th>
                    <th>Price (EUR)</th>
                    <th>Price (HUF)</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicleCategories.filter((cat) => cat.active === 1).map((category) => (
                    <tr key={category.Id}>
                      <td>
                        <strong>{category.name}</strong>
                        <br />
                        <small className="text-muted">
                          {category.capacity}
                          {' '}
                          pax,
                          {category.max_luggage}
                          {' '}
                          bags
                        </small>
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          step="0.01"
                          size="sm"
                          value={getCategoryPrice(category.Id, 'price_eur')}
                          onChange={(e) => handleCategoryPriceChange(category.Id, 'price_eur', e.target.value)}
                          placeholder="0.00"
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          step="1"
                          size="sm"
                          value={getCategoryPrice(category.Id, 'price_huf')}
                          onChange={(e) => handleCategoryPriceChange(category.Id, 'price_huf', e.target.value)}
                          placeholder="0"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {vehicleCategories.length === 0 && (
                <div className="alert alert-warning">
                  No vehicle categories found. Please create vehicle categories first.
                </div>
              )}
            </Form>
          </Col>

          <Col lg={6}>
            <div className="border rounded p-2 bg-light mb-3">
              <h6 className="mb-2">
                <i className="fa fa-map me-2" />
                Coverage Areas Preview
              </h6>
              {!showPreview ? (
                <div
                  className="d-flex align-items-center justify-content-center text-muted"
                  style={{ height: '350px', backgroundColor: '#e9ecef' }}
                >
                  <div className="text-center">
                    <i className="fa fa-map-marker-alt fa-3x mb-2 opacity-50" />
                    <p className="mb-0">Select origin and destination to preview coverage areas</p>
                  </div>
                </div>
              ) : (
                <ServiceAreaPreviewMap
                  originGeo={originGeo}
                  destGeo={destGeo}
                  active={modalReady}
                  mapHeight="350px"
                />
              )}
            </div>
            {(originGeo || destGeo) && (
              <div className="d-flex gap-3 flex-wrap small">
                {originGeo && (
                  <div>
                    <Badge bg="success" className="me-1">Origin</Badge>
                    <strong>{originGeo.name}</strong>
                    <span className="text-muted ms-1">
                      (
                      {originGeo.type === 'polygon' ? 'Polygon' : `${originGeo.radius_km} km`}
                      )
                    </span>
                  </div>
                )}
                {destGeo && (
                  <div>
                    <Badge bg="danger" className="me-1">Destination</Badge>
                    <strong>{destGeo.name}</strong>
                    <span className="text-muted ms-1">
                      (
                      {destGeo.type === 'polygon' ? 'Polygon' : `${destGeo.radius_km} km`}
                      )
                    </span>
                  </div>
                )}
              </div>
            )}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? 'Saving...' : 'Save Route'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default PredefinedRouteModal;
